import React from 'react';
import { FaClipboard } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';

interface Section {
  key: string;
  title: string;
  isTextSection: boolean;
}

interface Props {
  content: Record<string, string | null>;
  sections: Section[];
}

const GeneratedContent: React.FC<Props> = ({ content, sections }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Copied to clipboard!', 'success');
    });
  };

  const renderTextSection = (text: string) => {
    // Split the numbered items if they exist
    const items = text.split('\n').filter((item) => item.trim());

    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between relative group p-3 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <span className="text-gray-700 dark:text-gray-300">{item}</span>
            <button
              onClick={() => copyToClipboard(item)}
              className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              title="Copy to clipboard"
            >
              <FaClipboard className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderHtmlContent = (htmlContent: string) => (
    <div className="relative group">
      <div
        className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
        dangerouslySetInnerHTML={{
          __html: htmlContent
            .replace(/^```html\n/, '')
            .replace(/\n```$/, '')
            .trim(),
        }}
      />
      <button
        onClick={() => copyToClipboard(htmlContent)}
        className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy to clipboard"
      >
        <FaClipboard className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
      </button>
    </div>
  );

  return (
    <div className="space-y-8 bg-white dark:bg-gray-800 rounded-lg p-6 mt-8">
      {sections.map(({ key, title, isTextSection }) => {
        if (!content[key]) return null;

        return (
          <div key={key} className="space-y-4">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{t(title)}</h2>
            </div>
            {isTextSection ? renderTextSection(content[key]!) : renderHtmlContent(content[key]!)}
          </div>
        );
      })}
    </div>
  );
};

export default GeneratedContent;
